import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "../../lib/i18n";

/**
 * Centered editorial About block — text-only, cinematic invitation.
 * Redirects to /about via CTA link.
 */
const AboutSection = ({ data }) => {
  const { pick, t, lang } = useI18n();
  const isRtl = lang === "ar";

  const ctaLabel = pick({
    tr: "Gelin, size hikayemizi anlatalım",
    en: "Come, let us tell you our story",
    ar: "تعال، دعنا نروي لك قصتنا",
  });

  return (
    <section className="relative bg-white overflow-hidden" data-testid="about-section">
      {/* Faint decorative marks — no images */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-full bg-brand-navy" />
      </div>

      <div className="container-x py-6 md:py-10 relative">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
            className="flex items-center justify-center gap-4 mb-10"
          >
            <span className="h-px w-12 bg-brand-primary" />
            <span className="text-[0.7rem] md:text-[0.72rem] tracking-[0.36em] uppercase text-brand-primary font-medium">
              {t("nav.about")}
            </span>
            <span className="h-px w-12 bg-brand-primary" />
          </motion.div>

          {/* Big statement */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif text-brand-navy leading-[1] tracking-tight"
            style={{ fontSize: "clamp(2rem, 5.5vw, 4.75rem)" }}
            data-testid="about-title"
          >
            {pick(data?.about_title, "")}
          </motion.h2>

          {/* Hairline divider */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.35, ease: [0.19, 1, 0.22, 1] }}
            className="h-px w-24 md:w-32 bg-brand-primary/30 mx-auto my-10 md:my-12"
          />

          {/* Body */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, delay: 0.2, ease: [0.19, 1, 0.22, 1] }}
            className="text-brand-dark leading-[1.75] mx-auto"
            style={{ fontSize: "clamp(1rem, 1.2vw, 1.15rem)", maxWidth: "60ch" }}
            data-testid="about-body"
          >
            {pick(data?.about_body, "")}
          </motion.p>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.19, 1, 0.22, 1] }}
            className="mt-12 md:mt-16"
          >
            <Link
              to="/about"
              data-testid="about-cta-link"
              className="group inline-flex items-center gap-4 md:gap-5 pl-6 pr-2 py-2 md:py-3 rounded-full border border-brand-navy/25 text-brand-navy hover:border-brand-navy transition-all duration-500 hover:pl-8"
            >
              <span
                className="font-serif italic leading-none"
                style={{ fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)" }}
              >
                {ctaLabel}
              </span>
              <span
                className={`flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-navy text-white transition-transform duration-500 ${
                  isRtl ? "group-hover:-translate-x-1 group-hover:-translate-y-1" : "group-hover:translate-x-1 group-hover:-translate-y-1"
                }`}
              >
                <ArrowUpRight size={18} strokeWidth={1.5} />
              </span>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
